import { useMemo, useState } from 'react'
import { clsx } from 'clsx'
import { Button } from 'design-system/ui/atoms/button'
import { AlertCircleIcon, User2Icon } from 'lucide-react'
import { Shimmer } from 'react-shimmer'

import type { GetInternalActionExecutionLogByIdResponse } from '~/generated/client'
import { DialogBoxCenter } from '~/design-system/atom/alert-dialog-sheet'
import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~/design-system/atom/dialog'
import { LinkComp } from '~/ui/components/base/link'
import { If } from '~/utils/reactComponent'
import { showFirstAndLastNLetters } from '~/utils/string'
import { formatTimeInMS, showDate } from '~/utils/time'
import {
  blockHeadingStyle,
  blockSubHeadingStyle,
  Dot,
  LineHeading,
} from '../components/common'
import { TabComponent } from '../components/tab'

const Row = ({
  label,
  value,
  isLast,
}: {
  label: string
  value: React.ReactNode
  isLast?: boolean
}) => (
  <tr
    className={clsx(
      'ds-border-b ds-py-6 ds-border-gray-100 ds-border-b-half ds-border-white-500 h-[40px] w-full',
      isLast && 'border-b-[0px]',
    )}
  >
    <td className="ds-px-16 ds-py-6 ds-text-13 ds-align-top min-h-[32px] text-black-400">
      {label}
    </td>
    <td className="ds-px-16 ds-py-6 ds-font-mono ds-text-13 ds-text-black-800 flex min-h-[40px] items-center justify-end pr-[16px]">
      {value}
    </td>
  </tr>
)

const PayloadAndSchemaModal = ({
  data,
  open,
  setOpen,
}: {
  data: GetInternalActionExecutionLogByIdResponse
  open: boolean
  setOpen: (open: boolean) => void
}) => {
  const tabResponse = useMemo(
    () => [
      {
        key: 'payload',
        heading: 'Payload passed',
        isJSON: true,
        content: JSON.stringify(data.payloadReceived),
      },
    ],
    [data.payloadReceived],
  )
  return (
    <DialogBoxCenter open={open} setOpen={setOpen} className="max-w-[600px]">
      <DialogHeader className="p-[20px] ">
        <DialogTitle className="ds-relative">
          <div className="ds-font-heading flex items-center gap-2 text-[15px] font-[500] leading-none text-black-200">
            <AlertCircleIcon height={16} width={16} strokeWidth={1.5} />
            Payload and Schema
          </div>
        </DialogTitle>
      </DialogHeader>
      <DialogDescription className="p-[20px] pt-[12px]">
        <TabComponent tabs={tabResponse} selectedTab="payload" />
      </DialogDescription>
    </DialogBoxCenter>
  )
}

const ActionHeaderTable = ({
  data,
}: {
  data: GetInternalActionExecutionLogByIdResponse
}) => {
  const [isSchemaOpen, setIsSchemaOpen] = useState(false)

  const sessionId = data.session?.sessionId

  return (
    <div className="ds-border-1 ds-border-gray-200 relative z-50 mt-4 w-full overflow-hidden rounded-[8px] bg-white">
      <If condition={isSchemaOpen}>
        <PayloadAndSchemaModal
          data={data}
          open={isSchemaOpen}
          setOpen={setIsSchemaOpen}
        />
      </If>
      <table className="ds-w-full">
        <tbody>
          <Row
            label="Log ID"
            value={
              !data ? (
                <Shimmer width={100} height={20} />
              ) : (
                <div>{data.actionLogId}</div>
              )
            }
          />
          <Row
            label="Entity & connection"
            value={
              <div className="flex items-center gap-2">
                {!data ? (
                  <Shimmer width={200} height={20} />
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      <User2Icon className="ds-w-12 ds-h-12" />
                      {data.connection.entity}
                    </div>
                    {data.connection.id ? ' | ' : ''}
                    {data.connection.id ? (
                      <LinkComp
                        href={`/connection/${data.connection.id}`}
                        variant="primary"
                      >
                        {showFirstAndLastNLetters(data.connection.id || '', 8)}
                      </LinkComp>
                    ) : null}
                  </>
                )}
              </div>
            }
          />
          {sessionId ? (
            <Row
              label="Session"
              value={
                <div className="ds-flex ds-items-center ds-gap-2">
                  {!data ? (
                    <Shimmer width={100} height={20} />
                  ) : (
                    <span className="mr-[8px]">{sessionId as string}</span>
                  )}
                </div>
              }
            />
          ) : null}
          <Row
            label="Language and SDK"
            value={
              <div className="ds-flex ds-items-center ds-gap-2">
                {!data ? (
                  <Shimmer width={100} height={20} />
                ) : data?.executionMetadata?.source &&
                  data?.executionMetadata?.framework ? (
                  <span>
                    {`${data.executionMetadata.source as string} and ${data.executionMetadata.framework as string}`}
                  </span>
                ) : (
                  <span>Not provided</span>
                )}
              </div>
            }
          />

          <Row
            isLast={true}
            label="Payload and Schema "
            value={
              !data ? (
                <Shimmer width={100} height={22} />
              ) : (
                <Button
                  size="28"
                  variant="secondary"
                  onClick={() => setIsSchemaOpen(true)}
                >
                  View Payload
                </Button>
              )
            }
          />
        </tbody>
      </table>
    </div>
  )
}

export const ActionHeaderBlock = ({
  data,
}: {
  data: GetInternalActionExecutionLogByIdResponse
}) => {
  return (
    <div className="ds-flex ds-items-center ds-gap-2 relative w-full">
      <Dot />
      <div className="w-full pl-[8px]">
        <div className="flex w-full flex-col gap-1">
          <LineHeading />
          {!data ? (
            <Shimmer width={200} height={20} />
          ) : (
            <div className={blockHeadingStyle}>Action Invoked</div>
          )}
          {!data ? (
            <Shimmer width={100} height={20} />
          ) : (
            <div className={blockSubHeadingStyle}>
              at{' '}
              <span className="ds-text-black-500">
                {showDate(new Date(data.endTime))}
              </span>{' '}
              | {formatTimeInMS(new Date(data.startTime))}
            </div>
          )}
          <div className="relative left-[-36px] w-[calc(100%+28px)]">
            <ActionHeaderTable data={data} />
          </div>
        </div>
      </div>
    </div>
  )
}
